/**
 * SS PDP features slider — <ss-pdp-features-slider>
 * Mobile/tablet: native scroll-snap row with dots, arrows and keyboard.
 * Desktop with `data-scroll-driven="true"`: the inner block is sticky and
 * the active slide follows the scroll progress of the tall wrapper (legacy
 * "scroll-sticky" behaviour). Optional autoplay. Videos are handled by
 * ss-media.js. No text here — aria labels come from the Liquid.
 */
(function () {
  'use strict';
  if (customElements.get('ss-pdp-features-slider')) return;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class SsPdpFeaturesSlider extends HTMLElement {
    constructor() {
      super();
      this._listeners = [];
      this.index = 0;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-ss-track]');
      this.slides = Array.from(this.querySelectorAll('.mattress-v2-video-slide'));
      this.dots = Array.from(this.querySelectorAll('.mattress-v2-video-nav-dot'));
      this.prev = this.querySelector('[data-ss-prev]');
      this.next = this.querySelector('[data-ss-next]');
      this.status = this.querySelector('[data-ss-status]');
      this.wrapper = this.querySelector('.scroll-sticky-wrapper');
      if (!this.track || !this.slides.length) return;

      this.desktop = window.matchMedia('(min-width: 990px)');
      this.dots.forEach(function (dot, i) { this.on(dot, 'click', function () { this.goTo(i, true); }.bind(this)); }, this);
      if (this.prev) this.on(this.prev, 'click', function () { this.goTo(this.index - 1, true); }.bind(this));
      if (this.next) this.on(this.next, 'click', function () { this.goTo(this.index + 1, true); }.bind(this));
      this.on(this.track, 'keydown', function (event) {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.index + 1, true); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.index - 1, true); }
      }.bind(this));
      this.on(this.track, 'scroll', this.onTrackScroll.bind(this), { passive: true });
      this.on(window, 'scroll', this.onWindowScroll.bind(this), { passive: true });
      this.on(window, 'resize', this.onWindowScroll.bind(this), { passive: true });
      this.on(this.track, 'pointerdown', function () { this.userInteracted = true; }.bind(this));
      this.setupAutoplay();
      this.update(0);
      this.onWindowScroll();
    }

    disconnectedCallback() {
      this._listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
      this._listeners = [];
      if (this.timer) clearInterval(this.timer);
    }

    on(el, type, fn, opts) { el.addEventListener(type, fn, opts); this._listeners.push([el, type, fn, opts]); }

    isScrollDriven() { return this.dataset.scrollDriven === 'true' && this.desktop.matches && !reduced; }

    goTo(i, byUser) {
      var count = this.slides.length;
      var target = ((i % count) + count) % count;
      if (byUser) this.userInteracted = true;
      if (this.isScrollDriven()) {
        /* Jump the page to the scroll position that shows this slide */
        var rect = this.wrapper.getBoundingClientRect();
        var total = this.wrapper.offsetHeight - window.innerHeight;
        var top = window.pageYOffset + rect.top + (total > 0 ? (target / count) * total + 1 : 0);
        window.scrollTo({ top: top, behavior: reduced ? 'auto' : 'smooth' });
        this.update(target);
        return;
      }
      var slide = this.slides[target];
      this.track.scrollTo({ left: slide.offsetLeft - (this.track.clientWidth - slide.clientWidth) / 2, behavior: reduced ? 'auto' : 'smooth' });
      this.update(target);
    }

    onTrackScroll() {
      if (this.isScrollDriven() || this._raf) return;
      this._raf = requestAnimationFrame(function () {
        this._raf = null;
        var center = this.track.scrollLeft + this.track.clientWidth / 2;
        var best = 0, dist = Infinity;
        this.slides.forEach(function (slide, i) {
          var c = slide.offsetLeft + slide.clientWidth / 2;
          var d = Math.abs(c - center);
          if (d < dist) { dist = d; best = i; }
        });
        if (best !== this.index) this.update(best);
      }.bind(this));
    }

    onWindowScroll() {
      if (!this.isScrollDriven()) { this.track.style.transform = ''; return; }
      var rect = this.wrapper.getBoundingClientRect();
      var total = this.wrapper.offsetHeight - window.innerHeight;
      if (total <= 0) return;
      var progress = Math.min(1, Math.max(0, -rect.top / total));
      var i = Math.min(this.slides.length - 1, Math.floor(progress * this.slides.length));
      if (i !== this.index) this.update(i);
      this.track.scrollLeft = this.slides[i].offsetLeft;
    }

    update(i) {
      this.index = i;
      this.dots.forEach(function (dot, k) {
        dot.classList.toggle('mattress-v2-video-active', k === i);
        dot.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });
      this.slides.forEach(function (slide, k) { slide.classList.toggle('is-active', k === i); });
      if (this.status) this.status.textContent = this.slides[i].getAttribute('aria-label') || '';
      if (this.prev) this.prev.disabled = i === 0;
      if (this.next) this.next.disabled = i === this.slides.length - 1;
    }

    setupAutoplay() {
      if (this.dataset.autoplay !== 'true' || reduced) return;
      var interval = parseInt(this.dataset.interval, 10) || 6000;
      this.timer = setInterval(function () {
        if (this.userInteracted || document.hidden || this.isScrollDriven()) return;
        var rect = this.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        this.goTo(this.index + 1, false);
      }.bind(this), interval);
    }
  }

  customElements.define('ss-pdp-features-slider', SsPdpFeaturesSlider);
})();
